import React from 'react'
import { Case, Default, Else, If, Switch, Then, When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { ResponsiveFlexRow } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { AsSpan, HeadingXS, ParagraphLG, ParagraphXL, TitleLG, TitleXS } from '@/atoms/Text'
import { slugs } from '@/constants'
import { UNITED_STATES } from '@/constants/regions'
import { useRegionContext } from '@/contexts/RegionContext'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import {
  isGuildExplainerLayout,
  isGuildSignupScrollingLayout,
  isGuildSignupMultiShowLayout,
  isGuildSignupSimpleLayout,
  isGuildSignupVideoLayout,
} from '@/services/GuildSignupPageService/validations'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { isUnitedStates } from '@/utils/location'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { GuildHowDoesItWorkV2 } from '@/views/GuildJoinView/GuildHowDoesItWorkV2'
import { GabrielCast } from '@/views/GuildSignupView/GabrielCast'
import { GuildNowStreamingRail } from '@/views/GuildSignupView/GuildBodySection/GuildNowStreamingRail'
import { GuildUpcomingRail } from '@/views/GuildSignupView/GuildBodySection/GuildUpcomingRail'
import { GuildEarlyAccessSection } from '@/views/GuildSignupView/GuildEarlyAccessSection'
import { GuildSimpleHero } from '@/views/GuildSignupView/GuildSimpleHero'
import { GuildVideoHero } from '@/views/GuildSignupView/GuildVideoHero'
import { KingOfKingsCalloutSection } from '@/views/GuildSignupView/KingOfKingsCalloutSection'
import { GuildApprovals } from '../GuildJoinView/GuildApprovals'
import { GuildHowDoesItWork } from '../GuildJoinView/GuildHowDoesItWork'
import { GuildPlanSelectionTiersStacked } from '../GuildJoinView/GuildPlanSelectionTiersStacked'
import { GuildPromotionBanner } from '../GuildJoinView/GuildPromotionBanner'
import { GuildBodySection } from './GuildBodySection'
import { GuildExplainerLayout } from './GuildExplainerLayout'
import { GuildMultiShowHero } from './GuildMultiShowHero'
import { GuildPredictsWinnersSection } from './GuildPredicutsWinnersSection'
import { GuildScrollingHero } from './GuildScrollingHero'
import { useGuildSignupContext } from './GuildSignupContext'
import { GuildSingleShowLayout } from './GuildSingleShowLayout'
import { GuildTestimonialSection } from './GuildTestimonialSection'

interface GuildSignupViewProps {
  hasUser: boolean
}

const comedyPages = new Set(['dry-bar-comedy', 'shayne-smith', 'adam-carolla'])

export const GuildSignupView: React.FC<GuildSignupViewProps> = ({ hasUser }) => {
  const { slug, layout, guildExplainerLayout, guildEarlyAccessSection, guildTestimonialSection, experiments, sale } =
    useGuildSignupContext()
  const { region } = useRegionContext()
  const { t } = useTranslate('guild')
  const isUS = isUnitedStates(region)

  const isMultiShowLayout = isGuildSignupMultiShowLayout(layout)
  const isVideoHeroLayout = isGuildSignupVideoLayout(layout)
  const isSimpleLayout = isGuildSignupSimpleLayout(layout)
  const isScrollingLayout = isGuildSignupScrollingLayout(layout)
  const canShowExplainerLayout = region === UNITED_STATES && isGuildExplainerLayout(guildExplainerLayout)

  const showPlanCards = Boolean(sale)

  return (
    <main className="h-full w-full se:overflow-x-hidden">
      <ThemeContextProvider isDarkMode={!isSimpleLayout}>
        <GuildPromotionBanner />
        <Switch>
          <Case condition={isVideoHeroLayout}>
            <GuildVideoHero hasUser={hasUser} />
          </Case>
          <Case condition={isMultiShowLayout}>
            <GuildMultiShowHero />
          </Case>
          <Case condition={isSimpleLayout}>
            <GuildSimpleHero hasUser={hasUser} />
          </Case>
          <Case condition={isScrollingLayout}>
            <GuildScrollingHero hasUser={hasUser} />
          </Case>
          <Default>
            <GuildSingleShowLayout />
          </Default>
        </Switch>
        <When
          condition={Boolean(
            experiments['king-of-kings-branded-landing-guild-section'] ||
              experiments['king-of-kings-guild-signup-section'],
          )}
        >
          <div className="-mb-2 bg-[#222222]">
            <KingOfKingsCalloutSection isScrollingLayout={isScrollingLayout} />
          </div>
        </When>
        <When condition={showPlanCards}>
          <div className="bg-[#222222]">
            <TitleLG weight="semibold" color="white" className="py-8 text-center lg:pt-16">
              <If condition={slug && comedyPages.has(slug)}>
                <Then>
                  <Translate i18nKey="readyToLaugh" t={t}>
                    Ready to laugh? Start now, cancel anytime.
                  </Translate>
                </Then>
                <Else>
                  <Translate i18nKey="supportFilmmakersCancelAnytime" t={t}>
                    Support films & filmmakers that share your values. Cancel anytime.
                  </Translate>
                </Else>
              </If>
            </TitleLG>
            <ResponsiveFlexRow className="mx-auto flex w-full max-w-[900px] gap-4 md:!items-stretch md:gap-8">
              <GuildPlanSelectionTiersStacked
                className="mt-6 max-w-[640px] sm:mx-auto md:mt-0 lg:max-w-[840px] lg:px-4"
                darkMode
                signupLayout
                hideLabels
                hidePremium={false}
              />
            </ResponsiveFlexRow>
          </div>
        </When>
        <When condition={slug === 'bitcoin-and-the-beast'}>
          <section className="relative bg-[#ffc409] py-12">
            <div className="relative">
              <div className="flex items-center bg-transparent">
                <div className="flex w-full flex-col items-center justify-end gap-4 p-4 sm:p-10 lg:flex-row se:p-2">
                  <div className="flex flex-1 justify-center">
                    <div className="w-fit rounded-full bg-white p-4">
                      <Image
                        className="rounded-full"
                        priority
                        width={500}
                        height={500}
                        alt="Robert Breedlove"
                        src={getCloudinaryImageUrl({
                          path: '/v1725139499/angel-studios/guild/robert-breedlove.webp',
                          transforms: 'c_fill',
                          width: 500,
                        })}
                      />
                    </div>
                  </div>
                  <div className="flex w-full flex-1 items-center justify-center">
                    <div className="flex max-w-lg flex-col justify-center gap-y-6">
                      <div className="flex flex-col gap-y-8">
                        <div className="rounded-2xl bg-white px-6 py-10">
                          <ParagraphXL className="mb-6">
                            &ldquo;The best way to improve the world is to educate children about the tremendous value
                            of individual freedom, rationality, and the pursuit of truth. To this end,{' '}
                            <span className="font-bold">
                              Tuttle Twins is an enlightening and entertaining tool for teaching children.
                            </span>
                            &rdquo;
                          </ParagraphXL>
                          <ParagraphXL>&mdash; Robert Breedlove</ParagraphXL>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </When>
        <When condition={canShowExplainerLayout}>
          {guildExplainerLayout && (
            <GuildExplainerLayout guildExplainerLayout={guildExplainerLayout} hasUser={hasUser} />
          )}
        </When>
        <When condition={Boolean(guildTestimonialSection && experiments['guild_testimonial_below_explainer_section'])}>
          <GuildTestimonialSection hasUser={hasUser} />
        </When>
        <When condition={!isSimpleLayout}>
          <div className="overflow-hidden">
            <GuildApprovals />
          </div>
        </When>
        <When condition={slug !== 'bitcoin-and-the-beast'}>
          <If condition={Boolean(experiments['project-focused-how-it-works-section'])}>
            <Then>
              <ThemeContextProvider isDarkMode={!isSimpleLayout}>
                <GuildHowDoesItWorkV2 />
              </ThemeContextProvider>
            </Then>
            <Else>
              <GuildHowDoesItWork hasUser={hasUser} />
            </Else>
          </If>
        </When>
        <When condition={slug === 'bitcoin-and-the-beast'}>
          <section className="relative bg-[#6E4799] py-12">
            <div className="relative">
              <div className="flex items-center bg-transparent">
                <div className="flex w-full flex-col items-center justify-end gap-4 p-4 sm:p-10 lg:flex-row lg:items-center lg:justify-center se:p-2">
                  <div className="flex w-full flex-1 items-center justify-center lg:order-1">
                    <div className="flex max-w-lg flex-col justify-center gap-y-6">
                      <div className="flex flex-col gap-y-8">
                        <div className="flex flex-col gap-6 rounded-2xl bg-white px-6 py-10">
                          <TitleLG weight="bold" className="mb-6">
                            Parent Vetted and Parent Approved
                          </TitleLG>
                          <ParagraphLG>A lot of the content made today is mindless or worse.</ParagraphLG>
                          <ParagraphLG>
                            Each episode of Tuttle Twins is parent-funded, parent-vetted and only released when it’s
                            parent-approved.
                          </ParagraphLG>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="lg:order-0 flex max-w-3xl flex-1 items-center justify-center">
                    <Image
                      priority
                      width={345}
                      height={800}
                      alt="Tuttle Twins Phone Image"
                      src={getCloudinaryImageUrl({
                        path: '/v1715183356/angel-studios/projects/tuttle-twins/the-tuttletwins-phone-facing-right-1.webp',
                        transforms: 'c_fill',
                        width: 345,
                      })}
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>
        </When>
        <When condition={slug === 'gabriel-and-the-guardians'}>
          <GabrielCast />
        </When>
        <When condition={Boolean(guildTestimonialSection && !experiments['guild_testimonial_below_explainer_section'])}>
          <GuildTestimonialSection hasUser={hasUser} />
        </When>
        <When condition={Boolean(isUS && guildEarlyAccessSection && !experiments['coming-soon-on-guild-signup'])}>
          <GuildEarlyAccessSection hasUser={hasUser} />
        </When>
        <GuildPredictsWinnersSection />
        <div className="bg-gradient-to-b from-core-gray-950 to-black pt-[104px]">
          <div className="grid grid-cols-12 ">
            <HeadingXS
              as={AsSpan}
              weight="bold"
              color="white"
              className="lg:photon-display col-span-12 col-start-1 col-end-13 mx-auto mb-4 self-center px-4 text-center lg:col-span-8 lg:col-start-3 lg:mb-8 lg:max-w-[900px] se:max-w-[350px] se:text-2xl"
            >
              <Translate i18nKey="moviesAndShowsYoullLove" t={t}>
                We&apos;re curating a library of shows you&apos;ll love.
              </Translate>
            </HeadingXS>
            <TitleXS
              as={AsSpan}
              weight="semibold"
              color="white"
              className="lg:photon-heading-sm col-span-10 col-start-2 col-end-12 mx-auto mb-16 max-w-[866px] self-center text-center lg:col-span-8 lg:col-start-3 lg:mb-16"
            >
              <Translate i18nKey="launchingNewTitlesEveryWeekV1" t={t}>
                We plan to launch more titles every week in 2025, thanks to Guild members like you.
              </Translate>
            </TitleXS>
          </div>
        </div>
        <div className="bg-gradient-to-b from-black to-core-gray-950 pb-[104px]">
          <section className="mb-12 overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
            <LazyHydrate whenVisible>
              <GuildNowStreamingRail
                featuredSlugs={
                  slug === slugs.gabrielAndTheGuardians
                    ? [
                        slugs.freelancers,
                        slugs.bonhoeffer,
                        slugs.youngDavid,
                        slugs.homesteadSeries,
                        slugs.homestead,
                        slugs.theShift,
                        slugs.tuttleTwins,
                        slugs.wingfeatherSaga,
                      ]
                    : undefined
                }
              />
            </LazyHydrate>
          </section>
          <section className="mb-12 overflow-x-hidden px-4 scrollbar-none sm:px-8 md:px-12 xl:px-16">
            <LazyHydrate whenVisible>
              <GuildUpcomingRail />
            </LazyHydrate>
          </section>
        </div>
        <GuildBodySection hasUser={hasUser} />
      </ThemeContextProvider>
    </main>
  )
}
